import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Bell, BellOff, ChevronDown, MapPin, Maximize2, MessageCircle, Minimize2, Send, Users, X } from 'react-feather';

import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from 'react-leaflet';

import { toast } from 'react-toastify';

import 'leaflet/dist/leaflet.css';

import api from '../Api';
import { getAuthUser } from '../../utils/auth';

import { getWorkOrderSocket } from '../../services/workOrderSocket';

import '../../assets/css/wo/ChatPetugas.css';

const DEFAULT_MAP_CENTER = [-6.17, 106.63];

/*
|--------------------------------------------------------------------------
| HELPER LOCATION
|--------------------------------------------------------------------------
*/

const normalizeLocation = (item = {}) => {
  const latitude = Number(item.latitude ?? item.gps_lat ?? item.lat);

  const longitude = Number(item.longitude ?? item.gps_long ?? item.lng ?? item.lon);

  return {
    user_id: item.user_id ?? item.id ?? null,

    username: item.username ?? item.name ?? '-',

    latitude,
    longitude,

    address: item.address ?? item.gps_address ?? null,

    updated_at: item.updated_at ?? null,
  };
};

/*
|--------------------------------------------------------------------------
| AUTO CENTER MAP
|--------------------------------------------------------------------------
*/

function MapAutoCenter({ locations }) {
  const map = useMap();

  useEffect(() => {
    if (!locations?.length) {
      return;
    }

    const first = locations[0];

    if (Number.isFinite(first.latitude) && Number.isFinite(first.longitude)) {
      map.setView([first.latitude, first.longitude], 14);
    }
  }, [locations, map]);

  return null;
}

/*
|--------------------------------------------------------------------------
| CHAT PETUGAS COMPONENT
|--------------------------------------------------------------------------
*/

function ChatPetugas({ workOrderId, initialWorkOrder = null, onClose }) {
  const id = workOrderId;

  const authUser = getAuthUser();

  const socketRef = useRef(null);

  const bottomRef = useRef(null);

  const typingTimerRef = useRef(null);

  /*
   * Digunakan listener Socket.IO supaya tidak perlu
   * re-register listener setiap kali minimize berubah.
   */
  const isMinimizedRef = useRef(false);

  const [connected, setConnected] = useState(false);

  const [joined, setJoined] = useState(false);

  const [loading, setLoading] = useState(true);

  const [sending, setSending] = useState(false);

  const [messages, setMessages] = useState([]);

  const [participants, setParticipants] = useState([]);

  const [presence, setPresence] = useState({});

  const [chatActivePresence, setChatActivePresence] = useState({});

  const [typingUsers, setTypingUsers] = useState({});

  const [locations, setLocations] = useState([]);

  const [workOrder, setWorkOrder] = useState(initialWorkOrder);

  const [message, setMessage] = useState('');

  const [isMinimized, setIsMinimized] = useState(false);

  const [isMaximized, setIsMaximized] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);

  const [notificationEnabled, setNotificationEnabled] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted',
  );

  /*
  |--------------------------------------------------------------------------
  | SYNC REF
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    isMinimizedRef.current = isMinimized;
  }, [isMinimized]);

  /*
  |--------------------------------------------------------------------------
  | RESET KETIKA GANTI TIKET
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    setWorkOrder(initialWorkOrder);

    setMessages([]);
    setParticipants([]);
    setTypingUsers({});
    setChatActivePresence({});
    setUnreadCount(0);
    setMessage('');
  }, [id, initialWorkOrder]);

  /*
  |--------------------------------------------------------------------------
  | LOCATION
  |--------------------------------------------------------------------------
  */

  const mergeLocation = useCallback((payload) => {
    const location = normalizeLocation(payload);

    if (!location.user_id || !Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) {
      return;
    }

    setLocations((prev) => {
      const filtered = prev.filter((item) => String(item.user_id) !== String(location.user_id));

      return [...filtered, location];
    });
  }, []);

  const participantIds = useMemo(() => new Set(participants.map((item) => String(item.id))), [participants]);

  const participantLocations = useMemo(
    () => locations.filter((item) => participantIds.has(String(item.user_id))),
    [locations, participantIds],
  );

  const onlineCount = useMemo(
    () => participants.filter((participant) => presence[String(participant.id)]?.is_online).length,
    [participants, presence],
  );

  /*
  |--------------------------------------------------------------------------
  | NOTIFICATION PERMISSION
  |--------------------------------------------------------------------------
  */

  const requestNotificationPermission = async () => {
    if (typeof Notification === 'undefined') {
      toast.warning('Browser ini tidak mendukung notifikasi.');

      return;
    }

    const result = await Notification.requestPermission();

    setNotificationEnabled(result === 'granted');

    if (result === 'granted') {
      toast.success('Notifikasi chat diaktifkan.');
    } else {
      toast.warning('Izin notifikasi tidak diberikan.');
    }
  };

  /*
  |--------------------------------------------------------------------------
  | NOTIFICATION SOUND
  |--------------------------------------------------------------------------
  */

  const playNotificationSound = useCallback(() => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;

      if (!AudioContextClass) {
        return;
      }

      const ctx = new AudioContextClass();

      const oscillator = ctx.createOscillator();

      const gain = ctx.createGain();

      oscillator.connect(gain);

      gain.connect(ctx.destination);

      oscillator.frequency.value = 780;

      gain.gain.value = 0.05;

      oscillator.start();

      setTimeout(() => {
        oscillator.stop();

        ctx.close();
      }, 130);
    } catch (error) {
      console.log('Audio notification diblokir browser.');
    }
  }, []);

  /*
  |--------------------------------------------------------------------------
  | BROWSER NOTIFICATION
  |--------------------------------------------------------------------------
  */

  const showBrowserNotification = useCallback((incomingMessage) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }

    const notification = new Notification('Pesan baru Work Order', {
      body: `${incomingMessage.sender_name || 'Petugas'}: ${incomingMessage.message}`,
    });

    notification.onclick = () => {
      window.focus();

      setIsMinimized(false);

      notification.close();
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | FETCH MESSAGES
  |--------------------------------------------------------------------------
  */

  const fetchMessages = useCallback(async () => {
    const response = await api.get(`/work-orders/${id}/messages`);

    setMessages(response.data?.data || []);
  }, [id]);

  /*
  |--------------------------------------------------------------------------
  | FETCH PARTICIPANTS
  |--------------------------------------------------------------------------
  */

  const fetchParticipants = useCallback(async () => {
    const response = await api.get(`/work-orders/${id}/chat-participants`);

    setParticipants(response.data?.data || []);
  }, [id]);

  /*
  |--------------------------------------------------------------------------
  | FETCH WORK ORDER DETAIL
  |--------------------------------------------------------------------------
  */

  const fetchWorkOrder = useCallback(async () => {
    try {
      const response = await api.get(`/work-orders/${id}/details`);

      const data = response.data?.data ?? response.data ?? null;

      /*
       * Kalau response detail ada,
       * gunakan response API.
       *
       * Kalau gagal/null,
       * initial row dari tabel tetap dipertahankan.
       */
      if (data) {
        setWorkOrder(data);
      }
    } catch (error) {
      console.error('Gagal mengambil detail tiket:', error);
    }
  }, [id]);

  /*
  |--------------------------------------------------------------------------
  | FETCH LOCATION
  |--------------------------------------------------------------------------
  */

  const fetchLocations = useCallback(async () => {
    try {
      const response = await api.get('/users/active-locations');

      const raw = response.data?.data ?? response.data ?? [];

      const arrayData = Array.isArray(raw) ? raw : [];

      const normalized = arrayData
        .map(normalizeLocation)
        .filter((item) => item.user_id && Number.isFinite(item.latitude) && Number.isFinite(item.longitude));

      setLocations(normalized);
    } catch (error) {
      console.error('Gagal mengambil lokasi:', error);
    }
  }, []);

  /*
  |--------------------------------------------------------------------------
  | LOAD INITIAL DATA
  |--------------------------------------------------------------------------
  */

  const loadInitialData = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);

      await Promise.all([fetchMessages(), fetchParticipants(), fetchWorkOrder(), fetchLocations()]);
    } catch (error) {
      console.error('Gagal memuat chat:', error);

      toast.error(error.response?.data?.message || 'Gagal memuat chat Work Order.');
    } finally {
      setLoading(false);
    }
  }, [id, fetchMessages, fetchParticipants, fetchWorkOrder, fetchLocations]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  /*
  |--------------------------------------------------------------------------
  | SOCKET.IO
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    const socket = getWorkOrderSocket();

    if (!socket) {
      toast.error('Token Socket.IO tidak ditemukan.');

      return undefined;
    }

    socketRef.current = socket;

    /*
    |--------------------------------------------------------------------------
    | JOIN CHAT
    |--------------------------------------------------------------------------
    */

    const joinChat = () => {
      socket.emit(
        'chat:join',
        {
          workOrderId: id,
        },
        (response) => {
          if (!response?.success) {
            toast.error(response?.message || 'Gagal masuk chat.');

            setJoined(false);

            return;
          }

          setJoined(true);
        },
      );

      /*
       * Ambil siapa saja yang sedang online.
       */
      socket.emit('presence:list', (response) => {
        if (!response?.success) {
          return;
        }

        const map = {};

        for (const item of response.data || []) {
          map[String(item.user_id)] = {
            is_online: true,
            last_seen: null,
          };
        }

        setPresence((prev) => ({
          ...prev,
          ...map,
        }));
      });
    };

    /*
    |--------------------------------------------------------------------------
    | CONNECT
    |--------------------------------------------------------------------------
    */

    const onConnect = () => {
      setConnected(true);

      joinChat();
    };

    /*
    |--------------------------------------------------------------------------
    | DISCONNECT
    |--------------------------------------------------------------------------
    */

    const onDisconnect = () => {
      setConnected(false);

      setJoined(false);
    };

    /*
    |--------------------------------------------------------------------------
    | CONNECT ERROR
    |--------------------------------------------------------------------------
    */

    const onConnectError = (error) => {
      console.error('Socket connect error:', error);

      setConnected(false);

      setJoined(false);
    };

    /*
    |--------------------------------------------------------------------------
    | MESSAGE
    |--------------------------------------------------------------------------
    */

    const onMessage = (incomingMessage) => {
      /*
       * Tambahkan message kalau belum ada.
       */
      setMessages((prev) => {
        const exists = prev.some((item) => String(item.id) === String(incomingMessage.id));

        if (exists) {
          return prev;
        }

        return [...prev, incomingMessage];
      });

      /*
       * Jangan notif pesan sendiri.
       */
      const isMine = String(incomingMessage.sender_id) === String(authUser?.id);

      if (isMine) {
        return;
      }

      /*
       * Notif kalau:
       *
       * - chat minimize
       * - browser/tab hidden
       * - browser tidak focus
       */
      const shouldNotify = isMinimizedRef.current || document.hidden || !document.hasFocus();

      if (shouldNotify) {
        setUnreadCount((prev) => prev + 1);

        playNotificationSound();

        showBrowserNotification(incomingMessage);
      }
    };

    /*
    |--------------------------------------------------------------------------
    | ONLINE / OFFLINE
    |--------------------------------------------------------------------------
    */

    const onPresence = (payload) => {
      setPresence((prev) => ({
        ...prev,

        [String(payload.user_id)]: {
          is_online: Boolean(payload.is_online),

          last_seen: payload.last_seen || prev[String(payload.user_id)]?.last_seen || null,
        },
      }));
    };

    /*
    |--------------------------------------------------------------------------
    | USER SEDANG MEMBUKA CHAT
    |--------------------------------------------------------------------------
    */

    const onChatPresence = (payload) => {
      setChatActivePresence((prev) => ({
        ...prev,

        [String(payload.user_id)]: Boolean(payload.active),
      }));
    };

    /*
    |--------------------------------------------------------------------------
    | TYPING
    |--------------------------------------------------------------------------
    */

    const onTyping = (payload) => {
      setTypingUsers((prev) => ({
        ...prev,

        [String(payload.user_id)]: Boolean(payload.is_typing),
      }));
    };

    /*
    |--------------------------------------------------------------------------
    | LOCATION
    |--------------------------------------------------------------------------
    */

    const onLocation = (payload) => {
      mergeLocation(payload);
    };

    /*
    |--------------------------------------------------------------------------
    | REGISTER LISTENERS
    |--------------------------------------------------------------------------
    */

    socket.on('connect', onConnect);

    socket.on('disconnect', onDisconnect);

    socket.on('connect_error', onConnectError);

    socket.on('chat:message', onMessage);

    socket.on('presence:update', onPresence);

    socket.on('chat:presence', onChatPresence);

    socket.on('chat:typing', onTyping);

    socket.on('location-update', onLocation);

    /*
     * Socket singleton bisa sudah connected
     * sebelum component dibuka.
     */
    if (socket.connected) {
      setConnected(true);

      joinChat();
    }

    /*
    |--------------------------------------------------------------------------
    | CLEANUP
    |--------------------------------------------------------------------------
    */

    return () => {
      /*
       * Berhenti typing saat chat ditutup.
       */
      socket.emit('chat:typing', {
        workOrderId: id,
        isTyping: false,
      });

      /*
       * Leave room.
       */
      socket.emit('chat:leave', {
        workOrderId: id,
      });

      /*
       * Hapus listener component ini.
       */
      socket.off('connect', onConnect);

      socket.off('disconnect', onDisconnect);

      socket.off('connect_error', onConnectError);

      socket.off('chat:message', onMessage);

      socket.off('presence:update', onPresence);

      socket.off('chat:presence', onChatPresence);

      socket.off('chat:typing', onTyping);

      socket.off('location-update', onLocation);
    };
  }, [id, authUser?.id, mergeLocation, playNotificationSound, showBrowserNotification]);

  /*
  |--------------------------------------------------------------------------
  | AUTO SCROLL
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (isMinimized) {
      return;
    }

    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
    });

    /*
     * Jangan otomatis menghilangkan unread
     * kalau browser sedang tidak aktif.
     */
    if (!document.hidden && document.hasFocus()) {
      setUnreadCount(0);
    }
  }, [messages, isMinimized]);

  /*
  |--------------------------------------------------------------------------
  | RESET UNREAD SAAT BROWSER FOCUS
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleWindowFocus = () => {
      if (!isMinimizedRef.current) {
        setUnreadCount(0);

        bottomRef.current?.scrollIntoView({
          behavior: 'smooth',
        });
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | CLEAR TYPING TIMER
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | SEND MESSAGE
  |--------------------------------------------------------------------------
  */

  const handleSend = () => {
    const text = message.trim();

    if (!text || !socketRef.current || !connected || !joined || sending) {
      return;
    }

    setSending(true);

    /*
     * Stop typing.
     */
    socketRef.current.emit('chat:typing', {
      workOrderId: id,
      isTyping: false,
    });

    socketRef.current.emit(
      'chat:send',
      {
        workOrderId: id,
        message: text,
      },
      (response) => {
        setSending(false);

        if (!response?.success) {
          toast.error(response?.message || 'Gagal mengirim pesan.');

          return;
        }

        setMessage('');
      },
    );
  };

  /*
  |--------------------------------------------------------------------------
  | MESSAGE CHANGE / TYPING
  |--------------------------------------------------------------------------
  */

  const handleMessageChange = (event) => {
    const value = event.target.value;

    setMessage(value);

    if (!socketRef.current) {
      return;
    }

    socketRef.current.emit('chat:typing', {
      workOrderId: id,

      isTyping: value.trim().length > 0,
    });

    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    typingTimerRef.current = setTimeout(() => {
      socketRef.current?.emit('chat:typing', {
        workOrderId: id,

        isTyping: false,
      });
    }, 1200);
  };

  /*
  |--------------------------------------------------------------------------
  | FORMAT TIME
  |--------------------------------------------------------------------------
  */

  const formatTime = (value) => {
    if (!value) {
      return '';
    }

    return new Date(value).toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /*
  |--------------------------------------------------------------------------
  | FORMAT LAST SEEN
  |--------------------------------------------------------------------------
  */

  const formatLastSeen = (value) => {
    if (!value) {
      return 'Offline';
    }

    return `Terakhir aktif ${new Date(value).toLocaleString('id-ID')}`;
  };

  /*
  |--------------------------------------------------------------------------
  | ROLE LABEL
  |--------------------------------------------------------------------------
  */

  const getRoleLabel = (participant) => {
    switch (participant.participant_type) {
      case 'kasie':
        return 'Kasie';

      case 'petugas':
        return 'Petugas';

      case 'kasie_petugas':
        return 'Kasie / Petugas';

      default:
        return 'User';
    }
  };

  /*
  |--------------------------------------------------------------------------
  | MINIMIZE
  |--------------------------------------------------------------------------
  */

  const handleToggleMinimize = () => {
    setIsMinimized((prev) => {
      const next = !prev;

      /*
       * Kalau minimize,
       * matikan maximize.
       */
      if (next) {
        setIsMaximized(false);
      }

      return next;
    });
  };

  /*
  |--------------------------------------------------------------------------
  | MAXIMIZE
  |--------------------------------------------------------------------------
  */

  const handleToggleMaximize = () => {
    setIsMinimized(false);

    setIsMaximized((prev) => !prev);
  };

  /*
  |--------------------------------------------------------------------------
  | CLOSE
  |--------------------------------------------------------------------------
  */

  const handleClose = () => {
    /*
     * Cleanup useEffect juga akan melakukan leave,
     * tapi emit di sini membuat status chat segera berubah.
     */
    socketRef.current?.emit('chat:typing', {
      workOrderId: id,
      isTyping: false,
    });

    socketRef.current?.emit('chat:leave', {
      workOrderId: id,
    });

    if (onClose) {
      onClose();
    }
  };

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <div className="workorder-chat-page">
      <div
        className={['workorder-chat-window', isMinimized ? 'chat-minimized' : '', isMaximized ? 'chat-maximized' : '']
          .filter(Boolean)
          .join(' ')}
      >
        {/* ==================================================
            HEADER
        ================================================== */}

        <div className="chat-header">
          <div className="chat-header-left">
            <div>
              <div className="chat-ticket-title">
                <MessageCircle size={17} />

                <span>Chat Petugas</span>

                {unreadCount > 0 && <span className="chat-unread-badge">{unreadCount}</span>}
              </div>

              <div className="chat-ticket-subtitle">
                {workOrder?.external_ref ||
                  workOrder?.work_order?.external_ref ||
                  initialWorkOrder?.external_ref ||
                  `WO ${id}`}

                <span className="mx-1">•</span>

                <span className={connected ? 'chat-online-text' : 'chat-offline-text'}>
                  {connected ? 'Realtime terhubung' : 'Terputus'}
                </span>
              </div>
            </div>
          </div>

          <div className="chat-header-actions">
            {/* NOTIFICATION */}

            <button
              type="button"
              className="chat-icon-button"
              onClick={requestNotificationPermission}
              title={notificationEnabled ? 'Notifikasi aktif' : 'Aktifkan notifikasi'}
            >
              {notificationEnabled ? <Bell size={17} /> : <BellOff size={17} />}
            </button>

            {/* MAXIMIZE */}

            {!isMinimized && (
              <button
                type="button"
                className="chat-icon-button"
                onClick={handleToggleMaximize}
                title={isMaximized ? 'Kembalikan ukuran' : 'Perbesar'}
              >
                <Maximize2 size={17} />
              </button>
            )}

            {/* MINIMIZE */}

            <button
              type="button"
              className="chat-icon-button"
              onClick={handleToggleMinimize}
              title={isMinimized ? 'Buka chat' : 'Minimize'}
            >
              {isMinimized ? <ChevronDown size={18} /> : <Minimize2 size={18} />}
            </button>

            {/* CLOSE */}

            <button
              type="button"
              className="chat-icon-button chat-close-button"
              onClick={handleClose}
              title="Tutup Chat"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ==================================================
            MINIMIZED
        ================================================== */}

        {isMinimized ? (
          <div className="chat-minimized-body" onClick={() => setIsMinimized(false)}>
            <div>
              <strong>{onlineCount}</strong> petugas online
            </div>

            {unreadCount > 0 && <div className="chat-minimized-unread">{unreadCount} pesan baru</div>}
          </div>
        ) : (
          /*
          |--------------------------------------------------------------------------
          | NORMAL / MAXIMIZED CHAT
          |--------------------------------------------------------------------------
          */

          <div className="chat-main">
            {/* ==================================================
                LEFT CHAT
            ================================================== */}

            <div className="chat-conversation">
              <div className="chat-messages">
                {/* LOADING */}

                {loading && (
                  <div className="chat-empty">
                    <MessageCircle size={40} />

                    <h6>Memuat Live Chat...</h6>
                  </div>
                )}

                {/* EMPTY */}

                {!loading && messages.length === 0 && (
                  <div className="chat-empty">
                    <MessageCircle size={42} />

                    <h6>Belum ada percakapan</h6>

                    <small>Mulai chat dengan petugas Work Order ini.</small>
                  </div>
                )}

                {/* MESSAGES */}

                {!loading &&
                  messages.map((item) => {
                    const mine = String(item.sender_id) === String(authUser?.id);

                    return (
                      <div key={item.id} className={`chat-message-row ${mine ? 'mine' : 'other'}`}>
                        {!mine && (
                          <div className="chat-avatar">{(item.sender_name || 'U').charAt(0).toUpperCase()}</div>
                        )}

                        <div className="chat-message-content">
                          {!mine && <div className="chat-sender-name">{item.sender_name || 'Petugas'}</div>}

                          <div className={`chat-bubble ${mine ? 'chat-bubble-mine' : 'chat-bubble-other'}`}>
                            {item.message}
                          </div>

                          <div className={`chat-message-time ${mine ? 'text-end' : ''}`}>
                            {formatTime(item.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                <div ref={bottomRef} />
              </div>

              {/* ==================================================
                  TYPING
              ================================================== */}

              <div className="chat-typing-container">
                {Object.entries(typingUsers)
                  .filter(([userId, typing]) => typing && String(userId) !== String(authUser?.id))
                  .map(([userId]) => {
                    const person = participants.find((participant) => String(participant.id) === String(userId));

                    return (
                      <span key={userId} className="chat-typing-text">
                        {person?.username || 'Petugas'} sedang mengetik...
                      </span>
                    );
                  })}
              </div>

              {/* ==================================================
                  INPUT CHAT
              ================================================== */}

              <div className="chat-input-area">
                <textarea
                  className="chat-message-input"
                  rows={1}
                  placeholder={connected && joined ? 'Tulis pesan...' : 'Menunggu koneksi...'}
                  value={message}
                  disabled={!connected || !joined || sending}
                  onChange={handleMessageChange}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();

                      handleSend();
                    }
                  }}
                />

                <button
                  type="button"
                  className="chat-send-button"
                  disabled={!message.trim() || !connected || !joined || sending}
                  onClick={handleSend}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>

            {/* ==================================================
                RIGHT SIDEBAR
            ================================================== */}

            <div className="chat-sidebar">
              {/* ==================================================
                  PARTICIPANTS
              ================================================== */}

              <div className="chat-sidebar-section">
                <div className="chat-sidebar-title">
                  <Users size={16} />
                  Petugas
                  <span className="chat-online-count">
                    {onlineCount}/{participants.length} online
                  </span>
                </div>

                <div className="chat-participant-list">
                  {participants.length === 0 && (
                    <div className="text-muted small">Belum ada petugas yang ditugaskan.</div>
                  )}

                  {participants.map((participant) => {
                    const userId = String(participant.id);

                    const online = presence[userId]?.is_online;

                    const chatActive = chatActivePresence[userId];

                    const typing = typingUsers[userId];

                    return (
                      <div key={participant.id} className="chat-participant">
                        <div className="participant-avatar-wrapper">
                          <div className="participant-avatar">
                            {(participant.username || 'U').charAt(0).toUpperCase()}
                          </div>

                          <span className={`participant-status-dot ${online ? 'online' : 'offline'}`} />
                        </div>

                        <div className="participant-info">
                          <div className="participant-name">{participant.username}</div>

                          <div className="participant-role">{getRoleLabel(participant)}</div>

                          {typing ? (
                            <div className="participant-typing">sedang mengetik...</div>
                          ) : chatActive ? (
                            <div className="participant-chat-active">Sedang membuka chat</div>
                          ) : online ? (
                            <div className="participant-online">Online</div>
                          ) : (
                            <div className="participant-offline">{formatLastSeen(presence[userId]?.last_seen)}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ==================================================
                  LIVE LOCATION
              ================================================== */}

              <div className="chat-sidebar-section chat-map-section">
                <div className="chat-sidebar-title">
                  <MapPin size={16} />
                  Lokasi Live
                </div>

                {participantLocations.length > 0 ? (
                  <MapContainer center={DEFAULT_MAP_CENTER} zoom={12} className="chat-map">
                    <TileLayer
                      attribution="&copy; OpenStreetMap contributors"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    <MapAutoCenter locations={participantLocations} />

                    {participantLocations.map((location) => (
                      <CircleMarker key={location.user_id} center={[location.latitude, location.longitude]} radius={9}>
                        <Popup>
                          <strong>{location.username}</strong>

                          <br />

                          {location.address || 'Lokasi petugas'}

                          <br />

                          <small>
                            {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                          </small>
                        </Popup>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                ) : (
                  <div className="chat-no-location">
                    <MapPin size={25} />

                    <span>Belum ada lokasi aktif petugas.</span>
                  </div>
                )}
              </div>

              {/* ==================================================
                  DETAIL TIKET
              ================================================== */}

              <div className="chat-sidebar-section">
                <div className="chat-sidebar-title">Detail Tiket</div>

                <div className="chat-detail-row">
                  <span>No Tiket</span>

                  <strong>
                    {workOrder?.external_ref ||
                      workOrder?.work_order?.external_ref ||
                      initialWorkOrder?.external_ref ||
                      '-'}
                  </strong>
                </div>

                <div className="chat-detail-row">
                  <span>Status</span>

                  <strong>
                    {workOrder?.status || workOrder?.work_order?.status || initialWorkOrder?.status || '-'}
                  </strong>
                </div>

                <div className="chat-detail-row">
                  <span>Pelanggan</span>

                  <strong>
                    {workOrder?.pelanggan_id ||
                      workOrder?.work_order?.pelanggan_id ||
                      initialWorkOrder?.pelanggan_id ||
                      '-'}
                  </strong>
                </div>

                <div className="chat-detail-row">
                  <span>Wilayah</span>

                  <strong>
                    {workOrder?.region_name ||
                      workOrder?.work_order?.region_name ||
                      initialWorkOrder?.region_name ||
                      '-'}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatPetugas;
